import { Box, Button } from '@mui/material';
import { type SaveAttendanceCodeInput, TuslaCode } from '@tyro/api';
import {
  ActionMenu,
  AttendanceCodeChip,
  AttendanceCodeSelectCellEditor,
  type BulkEditedRows,
  type GridOptions,
  type ICellRendererParams,
  PageContainer,
  PageHeading,
  Table,
  TableBooleanValue,
  TuslaCodeSelectCellEditor,
  commonActionMenuProps,
  useDebouncedValue,
} from '@tyro/core';
import { type TFunction, useTranslation } from '@tyro/i18n';
import { AddIcon, EditIcon, ReplyIcon, VerticalDotsIcon } from '@tyro/icons';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import {
  type ReturnTypeFromUseAttendanceCodes,
  useAttendanceCodes,
  useCreateOrUpdateAttendanceCode,
} from '../api';
import {
  EditAttendanceCodeModal,
  type EditAttendanceCodeViewProps,
} from '../components/edit-attendance-code-modal';
import { MergeAttendanceCodeModal } from '../components/merge-code-modal';

const tuslaCodes = Object.values(TuslaCode);

const getAttendanceCodeColumns = (
  t: TFunction<
    ('common' | 'attendance')[],
    undefined,
    ('attendance' | 'attendance')[]
  >,
  onClickEdit: Dispatch<
    SetStateAction<EditAttendanceCodeViewProps['initialAttendanceCodeState']>
  >,
  setAttendanceCodeToMerge: Dispatch<
    SetStateAction<ReturnTypeFromUseAttendanceCodes | null>
  >,
): GridOptions<ReturnTypeFromUseAttendanceCodes>['columnDefs'] => [
  {
    field: 'code',
    headerName: t('attendance:tuslaCode'),
    sort: 'asc',
    editable: true,
    lockVisible: true,
    cellEditorSelector: TuslaCodeSelectCellEditor(),
    onCellValueChanged: (params) => {
      if (
        params.newValue &&
        tuslaCodes.includes(params.newValue as TuslaCode)
      ) {
        params.node?.setDataValue(
          'description',
          t(`attendance:tuslaCodeDescription.${params.newValue as TuslaCode}`),
        );
      }
    },
  },
  {
    field: 'name',
    editable: true,
    headerName: t('attendance:attendanceCodeName'),
  },
  {
    field: 'description',
    headerName: t('common:description'),
    editable: ({ data }) => !(data?.code && tuslaCodes.includes(data?.code)),
  },
  {
    field: 'codeType',
    headerName: t('attendance:reportAs'),
    filter: true,
    editable: true,
    filterValueGetter: ({ data }) =>
      data?.codeType ? t(`common:attendanceCode.${data.codeType}`) : null,
    cellEditorSelector: AttendanceCodeSelectCellEditor(t),
    cellRenderer: ({
      data,
    }: ICellRendererParams<ReturnTypeFromUseAttendanceCodes>) =>
      data?.codeType ? <AttendanceCodeChip codeType={data?.codeType} /> : null,
  },
  {
    field: 'sessionCodeType',
    headerName: t('attendance:sessionCode'),
    filter: true,
    editable: true,
    filterValueGetter: ({ data }) =>
      data?.sessionCodeType
        ? t(`common:attendanceCode.${data.sessionCodeType}`)
        : null,
    cellEditorSelector: AttendanceCodeSelectCellEditor(t),
    cellRenderer: ({
      data,
    }: ICellRendererParams<ReturnTypeFromUseAttendanceCodes>) =>
      data?.sessionCodeType ? (
        <AttendanceCodeChip codeType={data?.sessionCodeType} />
      ) : null,
  },
  {
    field: 'visibleForTeacher',
    headerName: t('attendance:availableToTeachers'),
    editable: true,
    cellRenderer: ({
      data,
    }: ICellRendererParams<ReturnTypeFromUseAttendanceCodes>) => (
      <TableBooleanValue value={Boolean(data?.visibleForTeacher)} />
    ),
  },
  {
    field: 'visibleForContact',
    headerName: t('attendance:availableToContacts'),
    editable: true,
    cellRenderer: ({
      data,
    }: ICellRendererParams<ReturnTypeFromUseAttendanceCodes>) => (
      <TableBooleanValue value={Boolean(data?.visibleForContact)} />
    ),
  },
  {
    field: 'active',
    headerName: t('common:active'),
    editable: true,
    cellRenderer: ({
      data,
    }: ICellRendererParams<ReturnTypeFromUseAttendanceCodes>) => (
      <TableBooleanValue value={Boolean(data?.active)} />
    ),
  },
  {
    ...commonActionMenuProps,
    cellRenderer: ({
      data,
    }: ICellRendererParams<ReturnTypeFromUseAttendanceCodes>) =>
      data && (
        <ActionMenu
          iconOnly
          buttonIcon={<VerticalDotsIcon />}
          menuItems={[
            {
              label: t('attendance:editAttendanceCode'),
              onClick: () => onClickEdit(data),
              icon: <EditIcon />,
              disabled: !data?.custom,
              disabledTooltip: t('attendance:editAttendanceCodeTooltip'),
            },
            {
              label: t('attendance:mergeCodeIntoAnother'),
              onClick: () => setAttendanceCodeToMerge(data),
              icon: <ReplyIcon sx={{ transform: 'scaleX(-1)' }} />,
              hasAccess: ({ isTyroUser }) => isTyroUser,
              disabled: !data?.custom,
              disabledTooltip: t('attendance:mergeAttendanceCodeTooltip'),
            },
          ]}
        />
      ),
  },
];

export default function Codes() {
  const { t } = useTranslation(['common', 'attendance']);
  const { data: attendanceCodes } = useAttendanceCodes({});

  const { mutateAsync: saveBulkAttendanceCodes } =
    useCreateOrUpdateAttendanceCode();

  const {
    value: editAttendanceCodeInitialState,
    debouncedValue: debouncedEditAttendanceCodeInitialState,
    setValue: setEditAttendanceCodeInitialState,
  } = useDebouncedValue<
    EditAttendanceCodeViewProps['initialAttendanceCodeState']
  >({
    defaultValue: null,
  });
  const {
    value: attendanceCodeToMerge,
    debouncedValue: debouncedAttendanceCodeToMerge,
    setValue: setAttendanceCodeToMerge,
  } = useDebouncedValue<ReturnTypeFromUseAttendanceCodes | null>({
    defaultValue: null,
  });

  const attendanceCodeColumns = useMemo(
    () =>
      getAttendanceCodeColumns(
        t,
        setEditAttendanceCodeInitialState,
        setAttendanceCodeToMerge,
      ),
    [t, setEditAttendanceCodeInitialState, setAttendanceCodeToMerge],
  );

  const handleCreateAttendanceCode = () => {
    setEditAttendanceCodeInitialState(
      {} as EditAttendanceCodeViewProps['initialAttendanceCodeState'],
    );
  };

  const handleBulkSave = (
    data: BulkEditedRows<
      ReturnTypeFromUseAttendanceCodes,
      | 'name'
      | 'code'
      | 'codeType'
      | 'sessionCodeType'
      | 'description'
      | 'visibleForTeacher'
      | 'visibleForContact'
    >,
  ) => {
    const dataForEndpoint = Object.keys(data).map<SaveAttendanceCodeInput>(
      (id) => {
        const currentData = attendanceCodes?.find(
          (item) => item?.id === Number(id),
        );
        return {
          id: Number(id),
          code: data[id].code?.newValue ?? currentData?.code,
          name: data[id].name?.newValue
            ? [{ locale: 'en', value: data[id].name?.newValue }]
            : [{ locale: 'en', value: currentData?.name }],
          description: data[id].description?.newValue
            ? [
                {
                  locale: 'en',
                  value: data[id].description?.newValue,
                },
              ]
            : [
                {
                  locale: 'en',
                  value: currentData?.description,
                },
              ],
          codeType: data[id].codeType?.newValue ?? currentData?.codeType,
          sessionCodeType:
            data[id].sessionCodeType?.newValue ?? currentData?.sessionCodeType,
          visibleForTeacher:
            data[id].visibleForTeacher?.newValue ??
            currentData?.visibleForTeacher,
          visibleForContact:
            data[id].visibleForContact?.newValue ??
            currentData?.visibleForContact,
          nameTextId: currentData?.nameTextId,
          isActive: currentData?.active,
        };
      },
    );
    return saveBulkAttendanceCodes(dataForEndpoint);
  };

  return (
    <PageContainer title={t('attendance:attendanceCodes')}>
      <PageHeading
        title={t('attendance:attendanceCodes')}
        titleProps={{ variant: 'h3' }}
        rightAdornment={
          <Box display="flex" alignItems="center">
            <Button
              variant="contained"
              onClick={handleCreateAttendanceCode}
              startIcon={<AddIcon />}
            >
              {t('attendance:createAttendanceCode')}
            </Button>
          </Box>
        }
      />
      <Table
        rowData={attendanceCodes ?? []}
        columnDefs={attendanceCodeColumns}
        rowSelection="multiple"
        getRowId={({ data }) => String(data?.id)}
        onBulkSave={handleBulkSave}
      />
      <EditAttendanceCodeModal
        attendanceCodes={attendanceCodes ?? []}
        initialAttendanceCodeState={
          editAttendanceCodeInitialState ||
          debouncedEditAttendanceCodeInitialState
        }
        onClose={() => setEditAttendanceCodeInitialState(null)}
      />
      <MergeAttendanceCodeModal
        isOpen={!!attendanceCodeToMerge}
        onClose={() => setAttendanceCodeToMerge(null)}
        attendanceCodeToMergeFrom={
          attendanceCodeToMerge ?? debouncedAttendanceCodeToMerge
        }
      />
    </PageContainer>
  );
}
